import { ReportInvestmentIncomeTotals } from "../../../../../services";
import { formatCurrency } from "../../../../../utils";
import {
  INVESTMENT_INCOME_ACCOUNT_COLUMNS,
  INVESTMENT_INCOME_BY_ACCOUNT_TITLE,
  INVESTMENT_INCOME_BY_COUNTRY_TITLE,
  INVESTMENT_INCOME_COUNTRY_COLUMNS,
  INVESTMENT_INCOME_EMPTY_NOTE,
  INVESTMENT_INCOME_TITLE,
  MANUAL_ACCOUNTS_EXCLUDED_NOTE,
} from "../../constants";
import { IncomeHeaderRow } from "./IncomeHeaderRow";
import {
  IncomeCell,
  IncomeTable,
  IncomeTotalRow,
  LedgerNote,
  Section,
  SectionTitle,
  SubSection,
  SubSectionTitle,
  TableScroll,
} from "./styles";
import { InvestmentIncomeSectionProps } from "./types";

const incomeFiguresOf = (totals: ReportInvestmentIncomeTotals) => [
  totals.grossDividends,
  totals.dividendTaxWithheld,
  totals.netDividends,
  totals.grossInterest,
  totals.interestTaxWithheld,
  totals.netInterest,
];

export const InvestmentIncomeSection: React.FC<
  InvestmentIncomeSectionProps
> = ({ income }) => {
  const hasIncome = income.byAccount.length > 0;
  const hasCountries = income.byCountry.length > 0;
  const hasManualAccounts = income.manualAccountCount > 0;

  return (
    <Section data-testid="report-investment-income">
      <SectionTitle>{INVESTMENT_INCOME_TITLE}</SectionTitle>

      {!hasIncome && <LedgerNote>{INVESTMENT_INCOME_EMPTY_NOTE}</LedgerNote>}

      {hasIncome && (
        <SubSection>
          <SubSectionTitle>
            {INVESTMENT_INCOME_BY_ACCOUNT_TITLE}
          </SubSectionTitle>
          <TableScroll>
            <IncomeTable data-testid="report-income-by-account">
              <thead>
                <IncomeHeaderRow columns={INVESTMENT_INCOME_ACCOUNT_COLUMNS} />
              </thead>
              <tbody>
                {income.byAccount.map((account) => (
                  <tr key={account.accountId}>
                    <IncomeCell>{account.accountName}</IncomeCell>
                    {incomeFiguresOf(account).map((value, index) => (
                      <IncomeCell key={index} $numeric>
                        {formatCurrency(value)}
                      </IncomeCell>
                    ))}
                  </tr>
                ))}
                <IncomeTotalRow>
                  <IncomeCell>Total</IncomeCell>
                  {incomeFiguresOf(income.totals).map((value, index) => (
                    <IncomeCell key={index} $numeric>
                      {formatCurrency(value)}
                    </IncomeCell>
                  ))}
                </IncomeTotalRow>
              </tbody>
            </IncomeTable>
          </TableScroll>
        </SubSection>
      )}

      {hasCountries && (
        <SubSection>
          <SubSectionTitle>
            {INVESTMENT_INCOME_BY_COUNTRY_TITLE}
          </SubSectionTitle>
          <TableScroll>
            <IncomeTable data-testid="report-income-by-country">
              <thead>
                <IncomeHeaderRow columns={INVESTMENT_INCOME_COUNTRY_COLUMNS} />
              </thead>
              <tbody>
                {income.byCountry.map((country) => (
                  <tr key={country.countryCode ?? "unknown"}>
                    <IncomeCell>{country.countryName}</IncomeCell>
                    <IncomeCell $numeric>
                      {formatCurrency(country.grossDividends)}
                    </IncomeCell>
                    <IncomeCell $numeric>
                      {formatCurrency(country.taxWithheld)}
                    </IncomeCell>
                    <IncomeCell $numeric>
                      {formatCurrency(country.netDividends)}
                    </IncomeCell>
                  </tr>
                ))}
              </tbody>
            </IncomeTable>
          </TableScroll>
        </SubSection>
      )}

      {hasManualAccounts && (
        <LedgerNote data-testid="report-income-manual-note">
          {MANUAL_ACCOUNTS_EXCLUDED_NOTE}
        </LedgerNote>
      )}
    </Section>
  );
};
